/**
 * Sign-Up Sheets API Service (ported from flock-expo/src/services/signupSheets.service.ts)
 */

import { api } from '@/lib/apiClient';
import type {
  SignupSheet,
  CreateSheetPayload,
  AddSlotPayload,
  SignupSlot,
  SignupEntry,
} from '@flock-types/signupSheets.types';

/**
 * Pin a request to a specific organization (cross-org group rail). The backend
 * verifies membership of the org named in the header, so this is safe for any
 * org the user belongs to. Omitted → apiClient falls back to the current org.
 */
function orgConfig(organizationId?: string) {
  return organizationId ? { headers: { 'X-Organization-Id': organizationId } } : undefined;
}

interface SheetsListResponse {
  success: boolean;
  data: SignupSheet[];
}

interface SheetResponse {
  success: boolean;
  data: SignupSheet;
}

interface SlotResponse {
  success: boolean;
  data: SignupSlot;
}

interface SlotsResponse {
  success: boolean;
  data: SignupSlot[];
}

interface EntryResponse {
  success: boolean;
  data: SignupEntry;
}

interface MutationResponse {
  success: boolean;
  message?: string;
}

export type SheetUpdates = Partial<
  Pick<SignupSheet, 'title' | 'description' | 'status' | 'is_pinned'>
>;

// label/slot_date/slot_time accept null to clear the field on the backend
export interface SlotUpdates {
  label?: string | null;
  slot_date?: string | null;
  slot_time?: string | null;
  capacity?: number;
  sort_order?: number;
}

export const signupSheetsService = {
  /** Get all sign-up sheets for a group */
  async getSheets(
    groupId: string,
    status: 'active' | 'archived' = 'active',
    organizationId?: string
  ): Promise<SignupSheet[]> {
    const response = await api.get<SheetsListResponse>(
      `/groups/${groupId}/signup-sheets?status=${status}`,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Get a single sheet with all slots and entries */
  async getSheetDetail(sheetId: string, organizationId?: string): Promise<SignupSheet> {
    const response = await api.get<SheetResponse>(
      `/signup-sheets/${sheetId}`,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Create a new sign-up sheet */
  async createSheet(
    groupId: string,
    payload: CreateSheetPayload,
    organizationId?: string
  ): Promise<SignupSheet> {
    const response = await api.post<SheetResponse>(
      `/groups/${groupId}/signup-sheets`,
      payload,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Update a sheet (title, description, status, is_pinned) */
  async updateSheet(
    sheetId: string,
    updates: SheetUpdates,
    organizationId?: string
  ): Promise<SignupSheet> {
    const response = await api.put<SheetResponse>(
      `/signup-sheets/${sheetId}`,
      updates,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Delete a sheet */
  async deleteSheet(sheetId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/signup-sheets/${sheetId}`, orgConfig(organizationId));
  },

  /** Add a slot to a sheet */
  async addSlot(
    sheetId: string,
    payload: AddSlotPayload,
    organizationId?: string
  ): Promise<SignupSlot> {
    const response = await api.post<SlotResponse>(
      `/signup-sheets/${sheetId}/slots`,
      payload,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Add multiple slots at once (backend caps at 52) */
  async addSlotsBulk(
    sheetId: string,
    slots: AddSlotPayload[],
    organizationId?: string
  ): Promise<SignupSlot[]> {
    const response = await api.post<SlotsResponse>(
      `/signup-sheets/${sheetId}/slots/bulk`,
      { slots },
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Update a slot */
  async updateSlot(
    slotId: string,
    updates: SlotUpdates,
    organizationId?: string
  ): Promise<SignupSlot> {
    const response = await api.put<SlotResponse>(
      `/signup-slots/${slotId}`,
      updates,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Delete all slots for a sheet */
  async deleteAllSlots(sheetId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(
      `/signup-sheets/${sheetId}/slots`,
      orgConfig(organizationId)
    );
  },

  /** Delete a slot */
  async deleteSlot(slotId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/signup-slots/${slotId}`, orgConfig(organizationId));
  },

  /** Vote for a survey option (swaps previous vote) */
  async vote(slotId: string, organizationId?: string): Promise<SignupEntry> {
    const response = await api.post<EntryResponse>(
      `/signup-slots/${slotId}/vote`,
      {},
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Sign up for a slot */
  async signUp(slotId: string, note?: string, organizationId?: string): Promise<SignupEntry> {
    const response = await api.post<EntryResponse>(
      `/signup-slots/${slotId}/signup`,
      note ? { note } : {},
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Withdraw from a slot */
  async withdraw(slotId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/signup-slots/${slotId}/signup`, orgConfig(organizationId));
  },

  /** Update an entry (note) */
  async updateEntry(
    entryId: string,
    note: string | null,
    organizationId?: string
  ): Promise<SignupEntry> {
    const response = await api.put<EntryResponse>(
      `/signup-entries/${entryId}`,
      { note },
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Delete an entry (leader removes someone) */
  async deleteEntry(entryId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/signup-entries/${entryId}`, orgConfig(organizationId));
  },

  /** Leader assigns a member to a slot */
  async assignMember(
    slotId: string,
    userId: string,
    organizationId?: string
  ): Promise<SignupEntry> {
    const response = await api.post<EntryResponse>(
      `/signup-slots/${slotId}/assign`,
      { userId },
      orgConfig(organizationId)
    );
    return response.data;
  },
};
